import {
    FC,
    ReactElement,
    useMemo,
    useRef,
    useState,
} from 'react';

import classNames from 'classnames';
import { useParams } from 'react-router-dom';

import { Wrapper } from '../../components';
import { Button, IconButton, SortInput } from '../../compositions';
import { MeilisearchAttribute } from '../../entities/@api/Meilisearch';
import { FormOption } from '../../entities/@forms/Form/Form';
import { ProductCategoryParams } from '../../entities/@products/ProductCategory/ProductCategory';
import { MeilisearchFilter, MeilisearchFilterType, MeilisearchRangeFilter } from '../../entities/@search/Meilisearch/Meilisearch';
import { getPriorityFiltersForCategory } from '../../entities/Filters/Filters';
import { sortWithPriority } from '../../helpers/sort';
import {
    useDeviceWidth,
    useHandleClickOutside,
    useHasPassedElementOnScroll,
    useTrans,
} from '../../hooks';
import { FilterAccordion, HighlightedFilter, RangeFilterAccordion } from './subcomponents';

import './ProductListFilters.scss';

interface ProductListFiltersProps {
    sidebarIsOpen: boolean;
    hideStickySidebar?: boolean;
    mayShowResetButton?: boolean;
    filters: MeilisearchFilter[];
    rangeFilters: MeilisearchRangeFilter[];
    highlightedFilters: MeilisearchFilter[];
    hiddenFilterAttributes?: MeilisearchAttribute[];
    resultsCount: number;
    sortOptions: FormOption[];
    onFilterSidebarToggle: (isOpen?: boolean) => void;
    onFiltersReset: () => void;
    className?: string;
}

export const ProductListFilters: FC<ProductListFiltersProps> = ({
    sidebarIsOpen,
    hideStickySidebar,
    mayShowResetButton,
    filters,
    rangeFilters,
    highlightedFilters,
    hiddenFilterAttributes = [],
    resultsCount,
    sortOptions,
    onFilterSidebarToggle,
    onFiltersReset,
    className = '',
}): ReactElement => {
    const trans = useTrans();
    const { slug: mainCategory, subCategory, subSubCategory } = useParams<ProductCategoryParams>();

    const { isMobile } = useDeviceWidth();

    const filterSearchThreshold = 36;

    const toggleButtonRef = useRef<HTMLButtonElement>(null);
    const anchorForFilterSidebarRef = useRef<HTMLDivElement>(null);
    const sidebarRef = useRef<HTMLDivElement>(null);

    const [activeHighlightedFilterId, setActiveHighlightedFilterId] = useState<MeilisearchAttribute>();

    const hasScrolledPassedFilters = useHasPassedElementOnScroll(anchorForFilterSidebarRef);
    const showStickySidebar = hasScrolledPassedFilters && !hideStickySidebar;

    const allowedFilters = useMemo((): MeilisearchFilter[] => (
        filters.filter(filter => !hiddenFilterAttributes.includes(filter.attribute))
    ), [filters, hiddenFilterAttributes]);

    const allowedRangeFilters = useMemo((): MeilisearchRangeFilter[] => (
        rangeFilters.filter(filter => !hiddenFilterAttributes.includes(filter.attribute))
    ), [rangeFilters, hiddenFilterAttributes]);

    const allowedHighlightedFilters = useMemo((): MeilisearchFilter[] => (
        highlightedFilters.filter(filter => !hiddenFilterAttributes.includes(filter.attribute))
    ), [highlightedFilters, hiddenFilterAttributes]);

    const sortedFilters = useMemo((): (MeilisearchFilter | MeilisearchRangeFilter)[] => {
        const combinedFilters = [...allowedFilters, ...allowedRangeFilters];
        const priorityAttributes = getPriorityFiltersForCategory(subSubCategory || subCategory || mainCategory);

        return sortWithPriority<MeilisearchFilter | MeilisearchRangeFilter, MeilisearchAttribute>(combinedFilters, 'attribute', priorityAttributes);
    }, [allowedFilters, allowedRangeFilters]);

    const handleToggleHighlightedFilter = (filterAttribute: MeilisearchAttribute): void => {
        setActiveHighlightedFilterId(activeHighlightedFilterId === filterAttribute ? undefined : filterAttribute);

        if (sidebarIsOpen) {
            onFilterSidebarToggle(false);
        }
    };

    const handleToggleSidebar = (): void => {
        if (activeHighlightedFilterId) {
            setActiveHighlightedFilterId(undefined);
        }

        onFilterSidebarToggle();
    };

    const handleFiltersReset = (): void => {
        onFilterSidebarToggle(false);
        onFiltersReset();
    };

    useHandleClickOutside([toggleButtonRef, sidebarRef], (): void => {
        if (sidebarIsOpen) {
            handleToggleSidebar();
        }
    });

    const wrapperClassNames = classNames('product-list-filters__wrapper', {
        'product-list-filters__wrapper--is-sticky': showStickySidebar,
    });

    const filterButtonClassNames = classNames('product-list-filters__filter-button', {
        'product-list-filters__filter-button--is-active': sidebarIsOpen,
    });

    const sidebarClassNames = classNames('product-list-filters__sidebar', {
        'product-list-filters__sidebar--is-active': sidebarIsOpen,
    });

    return (
        <div className={`product-list-filters ${className}`}>
            <div ref={anchorForFilterSidebarRef} className="product-list-filters__anchor" />

            {showStickySidebar && (
                <div className="product-list-filters__filter-wrapper-placeholder" />
            )}

            <Wrapper className={wrapperClassNames}>
                <div className="product-list-filters__filter-button-wrapper">
                    <IconButton
                        ref={toggleButtonRef}
                        isSmall
                        icon="filter"
                        text={isMobile ? trans('containers.productListFilters.filters') : trans('containers.productListFilters.allFilters')}
                        onClick={handleToggleSidebar}
                        className={filterButtonClassNames}
                    />

                    <aside
                        ref={sidebarRef}
                        inert={!sidebarIsOpen}
                        className={sidebarClassNames}
                    >
                        <div className="product-list-filters__top-wrapper">
                            <h6 className="product-list-filters__heading">
                                {trans('containers.productListFilters.allFilters')}
                            </h6>

                            <IconButton
                                isSmall
                                hideLabel
                                icon="cross"
                                text={trans('common.close')}
                                onClick={handleToggleSidebar}
                                className="product-list-filters__close-button"
                            />
                        </div>

                        {sortedFilters.map(filter => {
                            if (filter.type === MeilisearchFilterType.rangeFilter) {
                                return (
                                    <RangeFilterAccordion
                                        {...filter as MeilisearchRangeFilter}
                                        key={filter.attribute}
                                        className="product-list-filters__filter-accordion"
                                    />
                                );
                            }

                            return (
                                <FilterAccordion
                                    {...filter as MeilisearchFilter}
                                    key={filter.attribute}
                                    className="product-list-filters__filter-accordion"
                                />
                            );
                        })}

                        <div className="product-list-filters__button-wrapper">
                            <Button
                                isSmall
                                text={trans('containers.productListFilters.results', { count: resultsCount })}
                                onClick={handleToggleSidebar}
                                className="product-list-filters__results-button"
                            />

                            {mayShowResetButton && (
                                <Button
                                    isSmall
                                    text={trans('containers.productListFilters.resetFilters')}
                                    onClick={handleFiltersReset}
                                    className="product-list-filters__reset-button"
                                />
                            )}
                        </div>
                    </aside>
                </div>

                <div className="product-list-filters__highlighted-filters-wrapper">
                    {allowedHighlightedFilters.map(highlightedFilter => {
                        const highlightedFilterIsExpanded = activeHighlightedFilterId === highlightedFilter.attribute;
                        const handleToggle = () => handleToggleHighlightedFilter(highlightedFilter.attribute);

                        return (
                            <HighlightedFilter
                                {...highlightedFilter}
                                key={highlightedFilter.attribute}
                                isExpanded={highlightedFilterIsExpanded}
                                showSearchInput={highlightedFilter.options.length > filterSearchThreshold}
                                resultsCount={resultsCount}
                                searchInputLabel={trans('containers.productListFilters.searchPlaceholder')}
                                onToggle={handleToggle}
                            />
                        );
                    })}
                </div>

                <p className="product-list-filters__products-count">
                    {trans('containers.productListFilters.countProducts', { count: resultsCount })}
                </p>

                <SortInput
                    name="productSortingSelect"
                    options={sortOptions}
                    label={trans('containers.productListFilters.sortingLabel')}
                    placeholder={sortOptions[0].label}
                />
            </Wrapper>
        </div>
    );
};
